'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@upzi/web-shell/cn';

export function JobSearchToolbar({ children }: { children: ReactNode }) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 639px)');
    const sentinel = sentinelRef.current;

    if (!sentinel) return;

    let frame: number | null = null;
    let lastScrollY = window.scrollY;
    let direction = 0;
    let distance = 0;
    let isPinned = false;
    let isCollapsed = false;
    const updateCollapsed = (next: boolean) => {
      if (isCollapsed === next) return;
      isCollapsed = next;
      setCollapsed(next);
    };
    // Observe the boundary instead of forcing a layout read on every scroll.
    const observer = new IntersectionObserver(([entry]) => {
      const nextPinned = media.matches && entry.boundingClientRect.top < 0;

      if (isPinned === nextPinned) return;
      isPinned = nextPinned;
      setPinned(nextPinned);
      distance = 0;

      if (!nextPinned) updateCollapsed(false);
    });
    const update = () => {
      frame = null;
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;
      const nextDirection = Math.sign(delta);

      if (!isPinned) {
        updateCollapsed(false);
      } else if (nextDirection) {
        if (nextDirection !== direction) distance = 0;
        distance += Math.abs(delta);
        if (distance >= 8) {
          updateCollapsed(nextDirection > 0);
          distance = 0;
        }
      }
      if (nextDirection) direction = nextDirection;
      lastScrollY = scrollY;
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    const sync = () => {
      window.removeEventListener('scroll', onScroll);
      observer.disconnect();
      lastScrollY = window.scrollY;
      direction = 0;
      distance = 0;
      if (media.matches) {
        observer.observe(sentinel);
        window.addEventListener('scroll', onScroll, { passive: true });
      } else {
        isPinned = false;
        setPinned(false);
        updateCollapsed(false);
      }
      onScroll();
    };

    media.addEventListener('change', sync);
    sync();

    return () => {
      window.removeEventListener('scroll', onScroll);
      media.removeEventListener('change', sync);
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      <div ref={sentinelRef} className="-mb-px h-px sm:hidden" aria-hidden />
      <div
        className={cn(
          'bg-surface-pure sticky top-0 z-40 transition-transform duration-200 motion-reduce:transition-none sm:contents sm:transform-none [&>section]:relative',
          collapsed && '-translate-y-16',
          pinned && 'shadow-job-filter'
        )}
      >
        {children}
      </div>
    </>
  );
}
